import React, { useState } from 'react';
import * as R from 'ramda';
import {
  Datagrid,
  Labeled,
  FunctionField,
  SelectField,
  Tab,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, Card, CardContent, Grid, Typography } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import {
  AssessmentScoreChip,
  AssignmentGradeDistributionField,
  DateField,
  DeliveriesList,
  DeliveryTitle,
  NavigableTabs,
  OfflineList,
  GradeUpdatesProvider,
} from 'resources/common';
import LabsList from 'resources/common/LabsList';
import {
  AssignmentsProgressField,
  UnitProgressField,
  AssessmentsProgressField,
  LabsProgressField,
  QuizzesProgressField,
} from 'resources/common/cohorts/list/fields';
import { assignmentStatus } from 'data';
import RetakeAssessmentControl from './components/RetakeAssessmentControl';
import ByQuizzesSubtab from './ByQuizzesSubtab';

const ChallengeList = ({ record }) => {
  return (
    <OfflineList list={record.challenges} uniqueKey="challenge_id">
      <Datagrid>
        <TextField label="Challenge Name" source="title" />
        <SelectField
          label="Status"
          choices={assignmentStatus}
          source="status"
        />
        <FunctionField
          label="Submit Date"
          render={(challenge) => {
            if (R.propEq('status', 'pending', challenge)) return null;
            return <DateField record={challenge} source="date" />;
          }}
        />
        <FunctionField
          label="Score"
          render={(challenge) => {
            if (R.propEq('status', 'pending', challenge)) return null;
            return (
              <AssessmentScoreChip
                record={challenge}
                scoreSource="score"
                cutScoreSource="assessment_cut_score"
              />
            );
          }}
        />
      </Datagrid>
    </OfflineList>
  );
};

const ProgressTabContent = (props) => {
  const { record, course } = props;
  const [student, updateStudent] = useState(record);
  const location = useLocation();

  const assignments = R.pathOr([], [course, 'deliveries'], student);
  const challengeData = R.pathOr([], [course, 'challenges'], student);

  const { data: cohort } = useQuery({
    type: 'getOne',
    resource: record.is_b2b ? 'cohorts_b2b' : 'cohorts',
    payload: { id: record.cohort },
  });

  const assessments = R.pipe(
    R.groupBy(R.prop('assessment_id')),
    R.map(
      R.applySpec({
        challenge_count_header: R.length,
        title: R.path(['0', 'assessment_title']),
        assessment_id: R.path(['0', 'assessment_id']),
        assessment_cut_score: R.path(['0', 'assessment_cut_score']),
        path: R.path(['0', 'path']),
        score: R.ifElse(
          R.any(R.propEq('status', 'pending')),
          R.always(null),
          (challenges) =>
            Math.round(
              (R.path(['0', 'assessment_score_average'], challenges) +
                Number.EPSILON) *
                100
            ) / 100
        ),
        challenges: R.identity,
        required: R.path(['0', 'required']),
      })
    ),
    R.values
  )(challengeData);

  const labs = R.pathOr([], [course, 'labs'], student);

  const getModuleKey = (item) =>
    R.pathOr('Unknown module', ['path', 1, 'display_name'], item);
  const getSubmoduleKey = (item) =>
    R.pathOr('Unknown submodule', ['path', 2, 'display_name'], item);

  const assignmentsWithGrouping = assignments.map((assignment) => ({
    ...assignment,
    ...(assignment.path && assignment.path.length > 1
      ? {
          module: getModuleKey({ path: assignment.path }),
          submodule: getSubmoduleKey({ path: assignment.path }),
        }
      : {
          module: 'Unknown module',
          submodule: 'Unknown submodule',
        }),
  }));

  const assignmentsModules = R.pipe(
    R.groupBy(getModuleKey),
    R.toPairs,
    R.map(([moduleName, moduleAssignments]) => ({
      id: moduleName,
      display_name: moduleName,
      submodules: R.pipe(
        R.groupBy(getSubmoduleKey),
        R.toPairs,
        R.map(([submoduleName, submoduleAssignments]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          assignments: submoduleAssignments,
        }))
      )(moduleAssignments),
    }))
  )(assignmentsWithGrouping);

  const assessmentsWithGrouping = assessments.map((assessment) => ({
    ...assessment,
    ...(assessment.path && assessment.path.length > 1
      ? {
          module: getModuleKey({ path: assessment.path }),
          submodule: getSubmoduleKey({ path: assessment.path }),
        }
      : {
          module: 'Unknown module',
          submodule: 'Unknown submodule',
        }),
  }));

  const assessmentsModules = R.pipe(
    R.groupBy(getModuleKey),
    R.toPairs,
    R.map(([moduleName, moduleAssessments]) => ({
      id: moduleName,
      display_name: moduleName,
      submodules: R.pipe(
        R.groupBy(getSubmoduleKey),
        R.toPairs,
        R.map(([submoduleName, submoduleAssessments]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          assessments: submoduleAssessments,
        }))
      )(moduleAssessments),
    }))
  )(assessmentsWithGrouping);

  const labsWithGrouping = labs.map((lab) => ({
    ...lab,
    ...(lab.path && lab.path.length > 1
      ? {
          module: getModuleKey({ path: lab.path }),
          submodule: getSubmoduleKey({ path: lab.path }),
        }
      : {
          module: 'Unknown module',
          submodule: 'Unknown submodule',
        }),
  }));

  const labsModules = R.pipe(
    R.groupBy(getModuleKey),
    R.toPairs,
    R.map(([moduleName, moduleLabs]) => ({
      id: moduleName,
      display_name: moduleName,
      submodules: R.pipe(
        R.groupBy(getSubmoduleKey),
        R.toPairs,
        R.map(([submoduleName, submoduleLabs]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          labs: submoduleLabs,
        }))
      )(moduleLabs),
    }))
  )(labsWithGrouping);

  const isRequired = R.propEq('required', true);
  const averageAssessmentsScore = R.pipe(
    R.when(R.any(isRequired), R.filter(isRequired)),
    R.pluck('score'),
    R.reject(R.isNil),
    R.mean,
    (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
  )(assessments);

  const averageAssessmentsCutScore = R.pipe(
    R.when(R.any(isRequired), R.filter(isRequired)),
    R.pluck('assessment_cut_score'),
    R.reject(R.isNil),
    R.mean,
    (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
  )(assessments);

  const hasLabs = R.pipe(
    R.path([course, 'labs']),
    R.defaultTo([]),
    R.length,
    R.lt(0)
  )(student);

  // Keep the quizzes route mounted on deep-links; otherwise NavigableTabs 404s
  // while the cohort query is still loading and the tab is not in the Switch yet.
  const hasQuizzes =
    Boolean(cohort && R.hasPath([course, 'quizzes'], cohort)) ||
    R.endsWith('/quizzes', location.pathname || '');

  const {
    data: cohortQuizzes = [],
  } = useQuery({
    type: 'getManyReference',
    resource: 'quizzes',
    payload: {
      target: 'cohort',
      id: record.cohort,
      pagination: { perPage: 100 },
      filter: { course },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  const ModuleAssignmentsDetail = ({ record }) => {
    const flattenedAssignments = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'assignments')),
      R.sortBy((it) => R.pathOr('', ['path', 2, 'display_name'], it))
    )(record);

    return (
      <DeliveriesList
        label="Progress"
        type="students"
        record={{ deliveries: flattenedAssignments, pending: [] }}
        source="deliveries"
        defaultSort={{ field: 'path.2.display_name', order: 'ASC' }}
        course={course}
        studentId={student.id}
      />
    );
  };

  const ModuleAssessmentsDetail = ({ record }) => {
    const flattenedAssessments = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'assessments')),
      R.sortBy((it) => R.pathOr('', ['path', 2, 'display_name'], it))
    )(record);

    if (R.isEmpty(flattenedAssessments)) return null;

    return (
      <OfflineList list={flattenedAssessments} uniqueKey="assessment_id">
        <Datagrid rowClick="expand" expand={<ChallengeList />}>
          <DeliveryTitle />
          <TextField label="# of Challenges" source="challenge_count_header" />
          <AssessmentScoreChip
            label="Score"
            scoreSource="score"
            cutScoreSource="assessment_cut_score"
          />
          <RetakeAssessmentControl
            student={student}
            course={course}
            updateStudent={updateStudent}
          />
        </Datagrid>
      </OfflineList>
    );
  };

  const SubmoduleLabsList = ({ record }) => {
    const flattenedLabs = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'labs')),
      R.sortBy((it) => R.pathOr('', ['path', 2, 'display_name'], it))
    )(record);

    return (
      <LabsList
        label="Progress"
        type="students"
        record={{ labs: flattenedLabs, pending: [] }}
        source="labs"
        defaultSort={{ field: 'path.2.display_name', order: 'ASC' }}
        course={course}
      />
    );
  };

  const ModuleLabsDetail = ({ record }) => (
    <SubmoduleLabsList record={record} />
  );

  return (
    <Box px="16px">
      <Card style={{ margin: '15px 0px' }}>
        <CardContent>
          <Typography variant="h5" component="h3" gutterBottom>
            Progress
          </Typography>
          <Grid container spacing={2}>
            {R.pathOr(0, [course, 'learning_units_count'], student) > 0 && (
              <Grid item xs={2}>
                <Labeled label="Unit Progress" fullWidth>
                  <UnitProgressField
                    course={course}
                    target="progress"
                    record={student}
                    sortable={false}
                  />
                </Labeled>
              </Grid>
            )}
            <Grid item xs={5}>
              <Labeled
                label={`${
                  R.any(isRequired, assignments) ? 'Required ' : ''
                }Assignments`}
                fullWidth
              >
                <AssignmentsProgressField
                  course={course}
                  record={student}
                  cohort={cohort}
                />
              </Labeled>
            </Grid>
            {hasLabs && (
              <Grid item xs={5}>
                <Labeled
                  label={`${R.any(isRequired, labs) ? 'Required ' : ''}Labs`}
                  fullWidth
                >
                  <LabsProgressField
                    course={course}
                    record={student}
                    cohort={cohort}
                  />
                </Labeled>
              </Grid>
            )}
            {hasQuizzes && (
              <Grid item xs={5}>
                <Labeled label="Quizzes" fullWidth>
                  <QuizzesProgressField
                    course={course}
                    record={student}
                    cohort={cohort}
                    quizzes={cohortQuizzes}
                  />
                </Labeled>
              </Grid>
            )}
            <Grid item xs={5}>
              <Labeled
                label={`${
                  R.any(isRequired, assessments) ? 'Required Passed ' : ''
                }Assessments`}
                fullWidth
              >
                <AssessmentsProgressField
                  course={course}
                  record={student}
                  cohort={cohort}
                />
              </Labeled>
            </Grid>
            <Grid item xs={2}></Grid>
            <Grid item xs={5}>
              <Labeled label="Assignments Score Distribution" fullWidth>
                <AssignmentGradeDistributionField
                  label="Grades"
                  source={`${course}.deliveries`}
                  sortable={false}
                  record={student}
                />
              </Labeled>
            </Grid>
            <Grid item xs={5}>
              <Labeled label="Assessments Average Score" fullWidth>
                <AssessmentScoreChip
                  label="Cohort Average"
                  textAlign="center"
                  scoreSource="score"
                  cutScoreSource="cutScore"
                  record={{
                    score: averageAssessmentsScore,
                    cutScore: averageAssessmentsCutScore,
                  }}
                />
              </Labeled>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <NavigableTabs
        {...props}
        variant="fullWidth"
        matchUrl={R.pipe(R.split('/'), R.take(6), R.join('/'))}
      >
        <Tab
          label="Assignments"
          key="assignments"
          value="assignments"
          path="assignments"
        >
          <OfflineList list={assignmentsModules} uniqueKey="id">
            <Datagrid rowClick="expand" expand={<ModuleAssignmentsDetail />}>
              <TextField
                label="Module"
                source="display_name"
                sortable={false}
              />
            </Datagrid>
          </OfflineList>
        </Tab>
        {hasLabs && (
          <Tab label="Labs" key="labs" value="labs" path="labs">
            <OfflineList list={labsModules} uniqueKey="id">
              <Datagrid rowClick="expand" expand={<ModuleLabsDetail />}>
                <TextField
                  label="Module"
                  source="display_name"
                  sortable={false}
                />
              </Datagrid>
            </OfflineList>
          </Tab>
        )}
        {hasQuizzes && (
          <Tab
            label="By Quizzes"
            key="quizzes"
            value="quizzes"
            path="quizzes"
          >
            <ByQuizzesSubtab record={student} course={course} />
          </Tab>
        )}
        <Tab
          label="Assessments"
          key="assessments"
          value="assessments"
          path="assessments"
        >
          <OfflineList list={assessmentsModules} uniqueKey="id">
            <Datagrid rowClick="expand" expand={<ModuleAssessmentsDetail />}>
              <TextField
                label="Module"
                source="display_name"
                sortable={false}
              />
            </Datagrid>
          </OfflineList>
        </Tab>
      </NavigableTabs>
    </Box>
  );
};

const ProgressTab = (props) => (
  <GradeUpdatesProvider>
    <ProgressTabContent {...props} />
  </GradeUpdatesProvider>
);

export default ProgressTab;
